import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DollarSign, Search, Filter, Plus, X, ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { superAdminAPI } from '../../services/api';

const statusBadge = (status) => {
  const map = {
    paid: { label: 'مدفوع', cls: 'bg-emerald-100 text-emerald-700' },
    pending: { label: 'معلق', cls: 'bg-amber-100 text-amber-700' },
    failed: { label: 'فاشل', cls: 'bg-red-100 text-red-700' },
    refunded: { label: 'مسترد', cls: 'bg-gray-100 text-gray-700' },
  };
  const s = map[status] || { label: status || '-', cls: 'bg-gray-100 text-gray-700' };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
};

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [newPayment, setNewPayment] = useState({
    tenantId: '', amount: '', method: 'bank_transfer', reference: '', notes: ''
  });

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const response = await superAdminAPI.getPayments(params);
      const result = response.data?.data || response.data;
      setPayments(Array.isArray(result) ? result : result.items || []);
      setTotalPages(result.totalPages || result.pages || 1);
    } catch (error) {
      toast.error('فشل في تحميل المدفوعات');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, search]);

  const openRecordPayment = async () => {
    setShowModal(true);
    // Load tenants for dropdown
    try {
      const res = await superAdminAPI.getTenants({ limit: 200 });
      const data = res.data?.data || res.data;
      setTenants(Array.isArray(data) ? data : data.items || []);
    } catch (error) {
      console.error('Failed to load tenants:', error);
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!newPayment.tenantId || !newPayment.amount) {
      toast.error('يرجى ملء الحقول المطلوبة');
      return;
    }
    setSaving(true);
    try {
      await superAdminAPI.recordPayment({
        ...newPayment,
        amount: Number(newPayment.amount),
      });
      toast.success('تم تسجيل الدفعة');
      setShowModal(false);
      setNewPayment({ tenantId: '', amount: '', method: 'bank_transfer', reference: '', notes: '' });
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في تسجيل الدفعة');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">المدفوعات</h1>
          <p className="text-sm text-gray-500 mt-1">سجل جميع المدفوعات</p>
        </div>
        <button
          onClick={openRecordPayment}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          تسجيل دفعة
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="بحث..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pr-11 pl-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
        >
          <option value="">كل الحالات</option>
          <option value="paid">مدفوع</option>
          <option value="pending">معلق</option>
          <option value="failed">فاشل</option>
          <option value="refunded">مسترد</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-right px-6 py-3 font-medium text-gray-500">المختبر</th>
                <th className="text-right px-6 py-3 font-medium text-gray-500">المبلغ</th>
                <th className="text-right px-6 py-3 font-medium text-gray-500">الحالة</th>
                <th className="text-right px-6 py-3 font-medium text-gray-500 hidden md:table-cell">التاريخ</th>
                <th className="text-right px-6 py-3 font-medium text-gray-500 hidden md:table-cell">الطريقة</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {[...Array(5)].map((_, j) => (
                      <td key={j} className={`px-6 py-4 ${j >= 3 ? 'hidden md:table-cell' : ''}`}>
                        <div className="h-4 w-24 bg-gray-200 rounded" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-gray-400">
                    <DollarSign className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    لا توجد مدفوعات
                  </td>
                </tr>
              ) : (
                payments.map((payment) => (
                  <motion.tr
                    key={payment._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 font-medium text-gray-800">
                      {payment.tenantId?.name || payment.tenantName || '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-800 font-medium">
                      {payment.amount?.toLocaleString()} ر.س
                    </td>
                    <td className="px-6 py-4">{statusBadge(payment.status)}</td>
                    <td className="px-6 py-4 text-gray-500 hidden md:table-cell">
                      {payment.createdAt ? new Date(payment.createdAt).toLocaleDateString('ar-SA-u-ca-gregory') : '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-500 hidden md:table-cell">
                      {payment.method === 'bank_transfer' ? 'تحويل بنكي' :
                       payment.method === 'credit_card' ? 'بطاقة ائتمان' :
                       payment.method === 'cash' ? 'نقدي' :
                       payment.method || '-'}
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
            <p className="text-sm text-gray-500">صفحة {page} من {totalPages}</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40">
                <ChevronRight className="w-4 h-4" />
              </button>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40">
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Record Payment Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-800">تسجيل دفعة جديدة</h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleRecordPayment} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">المختبر *</label>
                  <select
                    value={newPayment.tenantId}
                    onChange={(e) => setNewPayment({ ...newPayment, tenantId: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                    required
                  >
                    <option value="">اختر المختبر</option>
                    {tenants.map((t) => (
                      <option key={t._id} value={t._id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">المبلغ (ر.س) *</label>
                  <input
                    type="number"
                    value={newPayment.amount}
                    onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min={0}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">طريقة الدفع</label>
                  <select
                    value={newPayment.method}
                    onChange={(e) => setNewPayment({ ...newPayment, method: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                  >
                    <option value="bank_transfer">تحويل بنكي</option>
                    <option value="credit_card">بطاقة ائتمان</option>
                    <option value="cash">نقدي</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">رقم المرجع</label>
                  <input
                    type="text"
                    value={newPayment.reference}
                    onChange={(e) => setNewPayment({ ...newPayment, reference: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ملاحظات</label>
                  <textarea
                    value={newPayment.notes}
                    onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'جاري التسجيل...' : 'تسجيل الدفعة'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200"
                  >
                    إلغاء
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PaymentHistory;
